import React, { Component } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Box,
  Button,
  TextField,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Typography,
  FormControl,
  ThemeProvider,
} from "@mui/material";
import {
  BulkOwsOverridePayload,
  BulkUpdatePayload,
  Dow,
  IBulkManualRateUpdate,
  IBulkRewardsUpdate,
  IManualRateUpdate,
  IRestrictionsUpdate,
} from "../../models/api.models";
import { YieldSheetService } from "../services";
import { BulkUpdatePreview } from "./bulk-update-preview";
import { restrictions } from "gn-shared";
import { format, isAfter, parseISO } from "date-fns";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import Popup from "../../reusable-components/popup/popup";
import { getFieldClasses } from "../common/forms";
import { YieldApi } from "api/yield";
import { connect } from "react-redux";
import { baseTheme } from "mui-theme";
import { ACTIONS, hasPermission } from "App/common/permissionHandler";

interface PublishPayload {
  start: string;
  end: string;
  baseRate: number;
  weekDays: boolean[];
  owslabel: string;
}

interface Period {
  start: string;
  end: string;
  idx: number;
}

interface FormState extends PublishPayload {
  property:
    | "baseRate"
    | "owsBaseRate"
    | "setRate"
    | "APPLY_STOP_SELL"
    | "REMOVE_STOP_SELL"
    | "APPLY_CLOSED_TO_ARRIVAL"
    | "REMOVE_CLOSED_TO_ARRIVAL"
    | "APPLY_CLOSED_TO_DEPARTURE"
    | "REMOVE_CLOSED_TO_DEPARTURE"
    | "APPLY_MIN_DAYS#2"
    | "REMOVE_MIN_DAYS"
    | "OPEN_REWARDS"
    | "CLOSE_REWARDS"
    | "OVERIDE_OWS";
  allDayCheked: boolean;
}

export interface State {
  toYieldSheet: boolean;
  displayWarning: boolean;
  displaySpinner: boolean;
  restrictionSelections: any;
  allDaySelected: boolean;
  periods: Period[];
  defaultOwsLabel: string;
  popupStatus: boolean;
  weekDays: boolean[];
  property: string;
  baseRate: number;
  errors: { [key: string]: string };
  owslabel: string;
  touched: { [key: string]: boolean };
  navigateToYieldSheet: boolean;
  level: string;
}

interface StateProps {
  activeHotel: any;
  user: any;
}

interface Props extends StateProps, bulkUpdateProps {}

interface bulkUpdateProps {
  isPopupOpen: boolean;
  popupState: (state: boolean) => void;
}

const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

class BulkUpdate extends Component<Props, State> {
  readonly state: State = {
    toYieldSheet: false,
    displayWarning: false,
    displaySpinner: false,
    restrictionSelections: {},
    allDaySelected: true,
    defaultOwsLabel: "Default",
    popupStatus: false,

    periods: [
      {
        idx: 0,
        start: format(new Date(), "yyyy-MM-dd"),
        end: format(new Date(), "yyyy-MM-dd"),
      },
    ],
    weekDays: [true, true, true, true, true, true, true],
    property: "baseRate",
    baseRate: 999,
    errors: {},
    owslabel: "Default",
    touched: {},
    navigateToYieldSheet: false,
    level: "baseRate",
  };

  constructor(props: Props) {
    super(props);

    this.state.baseRate = this.props.activeHotel.meta.defaultBar;
    this.applyToYieldSheet = this.applyToYieldSheet.bind(this);

    if (
      typeof this.props.activeHotel.cm.channelConfig.ows.label !== "undefined"
    ) {
      this.state.defaultOwsLabel =
        this.props.activeHotel.cm.channelConfig.ows.label;
    }
    this.state.popupStatus = this.props.isPopupOpen;
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.activeHotel !== this.props.activeHotel) {
      this.setState({ baseRate: this.props.activeHotel.meta.defaultBar });
      this.applyToYieldSheet = this.applyToYieldSheet.bind(this);

      if (
        typeof this.props.activeHotel.cm.channelConfig.ows.label !== "undefined"
      ) {
        this.setState({
          defaultOwsLabel: this.props.activeHotel.cm.channelConfig.ows.label,
        });
      }
      this.setState({ popupStatus: this.props.isPopupOpen });
    }
  }

  addPeriod = () => {
    const periods = this.state.periods;
    periods.push({
      idx: periods.length,
      start: format(new Date(), "yyyy-MM-dd"),
      end: format(new Date(), "yyyy-MM-dd"),
    });
    this.setState({ periods });
  };

  removePeriod = () => {
    const periods = this.state.periods;
    periods.pop();
    this.setState({ periods });
  };

  applyToYieldSheet(data: any) {
    this.setState({ navigateToYieldSheet: true });
    if (data.baseRate <= 0) {
      toast("Please check the highlited field", { type: "error" });
      return;
    } else {
      const formDataValues = data;
      this.setState({ displaySpinner: true });
      let payloads;
      let promises;
      switch (formDataValues.property) {
        case "OVERIDE_OWS":
          payloads = this.prepareOverideOwsPayload(formDataValues);
          promises = payloads.map((payload) =>
            YieldApi.bulkUpdateOwsOverride(payload),
          );
          Promise.all(promises)
            .then(() => {
              this.setState({ displayWarning: true, displaySpinner: false });
            })
            .catch((err: any) => {
              console.error(err);
              this.setState({ displaySpinner: false });
              toast.error("Error while updating OWS overrides");
            });
          break;
        case "baseRate":
          payloads = this.prepareBaseRatePayload(formDataValues);
          promises = payloads.map((payload) =>
            YieldApi.bulkUpdateRates(payload),
          );
          Promise.all(promises)
            .then(() => {
              this.setState({ displayWarning: true, displaySpinner: false });
            })
            .catch((err: any) => {
              console.error(err);
              this.setState({ displaySpinner: false });
              toast.error("Error while updating rates");
            });
          break;
        case "owsBaseRate":
          payloads = this.prepareOWSBaseRatePayload(formDataValues);
          promises = payloads.map((payload) =>
            YieldApi.bulkUpdateRates(payload),
          );
          Promise.all(promises)
            .then(() => {
              this.setState({ displayWarning: true, displaySpinner: false });
            })
            .catch((err: any) => {
              console.error(err);
              this.setState({ displaySpinner: false });
              toast.error("Error while updating rates");
            });
          break;
        case "setRate":
          payloads = this.prepareSetRatePayload(formDataValues);
          promises = payloads.map((payload) =>
            YieldSheetService.revanista.yieldsheet.days.bulkUpdateManualRates(
              payload,
              this.props,
            ),
          );
          Promise.all(promises)
            .then(() => {
              this.setState({ displayWarning: true, displaySpinner: false });
            })
            .catch((err: any) => {
              console.error(err);
              this.setState({ displaySpinner: false });
              toast.error("Error while updating rates");
            });
          break;
        case "OPEN_REWARDS":
        case "CLOSE_REWARDS":
          payloads = this.prepareRewardsPayload(formDataValues);
          promises = payloads.map((payload) =>
            YieldApi.bulkUpdateRewards(payload),
          );
          Promise.all(promises)
            .then(() => {
              this.setState({ displayWarning: true, displaySpinner: false });
            })
            .catch((err: any) => {
              console.error(err);
              this.setState({ displaySpinner: false });
              toast.error("Error while updating restrictions");
            });

          break;
        default:
          this.prepareRestrictionsPayload(formDataValues).then((payloads) => {
            promises = payloads.map((payload) =>
              YieldApi.bulkUpdateRestrictions(payload),
            );
            Promise.all(promises)
              .then(() => {
                this.setState({ displayWarning: true, displaySpinner: false });
              })
              .catch((err: any) => {
                console.error(err);
                this.setState({ displaySpinner: false });
                toast("Error while updating restrictions", { type: "error" });
              });
          });
      }
    }
  }

  discardAndExit() {
    this.props.popupState(false);
  }

  handleInputChange = (event: any) => {
    const { value } = event.target;
    this.setState({ baseRate: value });
    this.validate(value);
  };

  setStartDate = (date: Date | null, id: any) => {
    if (date) {
      this.setState((prevState) => {
        const periods = prevState.periods.map((period) => {
          if (period.idx === id.idx) {
            const newStartDate = format(date, "yyyy-MM-dd");
            // If the new start date is after the current end date, update the end date
            const currentEndDate = parseISO(period.end);
            if (isAfter(date, currentEndDate)) {
              return {
                ...period,
                start: newStartDate,
                end: newStartDate,
              };
            }
            return { ...period, start: newStartDate };
          }
          return period;
        });
        return { periods };
      });
    }
  };

  handleWeekDayChange = (index: any) => {
    this.setState((prevState) => {
      const newWeekDays = [...prevState.weekDays];
      newWeekDays[index] = !newWeekDays[index];
      return { weekDays: newWeekDays };
    });
  };

  setEndDate = (date: Date | null, id: any) => {
    if (date) {
      this.setState((prevState) => ({
        periods: prevState.periods.map((period) => {
          if (period.idx === id.idx) {
            const newEndDate = format(date, "yyyy-MM-dd");
            return { ...period, end: newEndDate };
          }
          return period;
        }),
      }));
    }
  };

  validate = (value: any) => {
    let error;
    if (!value) {
      error = "Required";
    } else if (value <= 0) {
      error = "Must be positive";
    }
    return error;
  };

  handleNativeSelect = (event: any) => {
    const rateLabel = event.target.value;
    getRateLabel(rateLabel);

    // Clear all selections when changing property type
    this.clearAllSelections();

    this.setState({
      property: rateLabel,
      level: event.target.value,
    });
  };

  // Update the clear selections method
  private clearAllSelections = () => {
    const { restrictionSelections } = this.state;
    const clearedSelections = { ...restrictionSelections };

    // Clear all room type selections and rate plan selections
    Object.keys(clearedSelections).forEach((roomType) => {
      clearedSelections[roomType] = {
        selected: false,
        ratePlans: Object.keys(
          clearedSelections[roomType].ratePlans || {},
        ).reduce((acc, ratePlan) => ({ ...acc, [ratePlan]: false }), {}),
      };
    });

    this.setState({ restrictionSelections: clearedSelections });

    console.log("Clearing all selections due to property change");
  };

  // Add method to check if selections are valid
  private hasValidSelections = (): boolean => {
    const { restrictionSelections, property } = this.state;

    // For properties that don't require selections, always return true
    const propertiesThatDontRequireSelections = [
      "baseRate",
      "owsBaseRate",
      "CLOSE_REWARDS",
      "OPEN_REWARDS",
      "OVERIDE_OWS",
    ];

    if (propertiesThatDontRequireSelections.includes(property)) {
      return true;
    }

    // Check if any room type is selected
    const hasRoomTypeSelected = Object.keys(restrictionSelections).some(
      (roomType) => restrictionSelections[roomType].selected,
    );

    // Check if any rate plan is selected
    const hasRatePlanSelected = Object.keys(restrictionSelections).some(
      (roomType) => {
        const roomTypeData = restrictionSelections[roomType];
        return (
          !roomTypeData.selected &&
          Object.keys(roomTypeData.ratePlans || {}).some(
            (ratePlan) => roomTypeData.ratePlans[ratePlan],
          )
        );
      },
    );

    return hasRoomTypeSelected || hasRatePlanSelected;
  };

  render() {
    if (this.state.toYieldSheet) {
      return <Navigate to={`/${this.props.activeHotel.hotelId}`} />;
    }

    let owsOverrideOptions: any = null;

    if (
      this.props.activeHotel?.cm?.channelConfig?.ows?.SeasonnalConfig?.formulas
    ) {
      owsOverrideOptions =
        this.props.activeHotel.cm.channelConfig.ows.SeasonnalConfig.formulas.map(
          (item: any) => (
            <option key={item.label} value={item.label}>
              {item.label}
            </option>
          ),
        );
    }

    const datePickerPopperStyles = `
      .react-datepicker-popper {
        z-index: 1500 !important; /* MUI Dialogs are around 1300. Popper needs to be higher. */
      }
    `;

    const isApplyDisabled = () => {
      // Check base rate validation for rate-related properties
      const baseRateError = this.validate(this.state.baseRate);
      const hasBaseRateError =
        (this.state.property === "baseRate" ||
          this.state.property === "setRate" ||
          this.state.property === "owsBaseRate") &&
        !!baseRateError;

      // Check if selections are valid
      const hasInvalidSelections = !this.hasValidSelections();

      return hasBaseRateError || hasInvalidSelections;
    };

    return (
      <>
        <style>{datePickerPopperStyles}</style>
        <Popup
          isPopupOpen={this.state.displayWarning}
          title="Applying changes to Yield Sheet..."
          content={[
            "Your changes will be processed in the background, it may take a moment to be reflected on the yield sheet.",
          ]}
          action={() => {
            this.setState({ displayWarning: false });
          }}
          actionName="Back to Bulk Update"
          cancelAction={() => {
            this.setState({ displayWarning: false });
            this.props.popupState(false);
          }}
          cancelName="Back to yield sheet"
          maxWidth="sm"
        />
        <Popup
          isPopupOpen={this.props.isPopupOpen}
          closeOnOutsideClick={true}
          title="Bulk Update"
          content={[
            <ThemeProvider theme={baseTheme} key="bulk-update-theme-provider">
              <Box
                component="div"
                sx={{
                  padding: 2,
                  width: "100%",
                }}
                key="bulk-update-content"
              >
                <Box className="row" sx={{ paddingRight: "1rem" }}>
                  <Box>
                    {this.state.periods.map((period: Period) => (
                      <Box
                        key={period.idx}
                        sx={{
                          width: "100%",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            marginBottom: "1rem",
                            marginLeft: "1rem",
                            marginRight: "3rem",
                            width: "100%",
                            gap: "1rem", // Added gap for spacing between date pickers
                          }}
                        >
                          {/* Updated DatePicker styling */}
                          <Box sx={{ position: "relative", flexGrow: 1 }}>
                            <InputLabel
                              sx={{
                                position: "absolute",
                                backgroundColor: (theme) =>
                                  theme.palette.background.paper,
                                padding: "0 4px",
                                top: "-8px",
                                left: "1rem", // Adjusted positioning
                                fontSize: "0.75rem",
                                zIndex: 10, // Reduced from 999999999999
                                color: "rgba(0, 0, 0, 0.6)",
                              }}
                            >
                              From
                            </InputLabel>
                            <DatePicker
                              selected={parseISO(period.start)}
                              onChange={(date: Date | null) => {
                                this.setStartDate(date, period);
                              }}
                              dateFormat="dd/MM/yyyy"
                              minDate={new Date()}
                              popperPlacement="bottom-start" // Kept for positioning
                              customInput={<TextField fullWidth size="small" />}
                            />
                          </Box>
                          {/* Updated DatePicker styling */}
                          <Box sx={{ position: "relative", flexGrow: 1 }}>
                            <InputLabel
                              sx={{
                                position: "absolute",
                                backgroundColor: (theme) =>
                                  theme.palette.background.paper,
                                padding: "0 4px",
                                top: "-8px",
                                left: "1rem", // Adjusted positioning
                                fontSize: "0.75rem",
                                zIndex: 10, // Reduced from 999999999999
                                color: "rgba(0, 0, 0, 0.6)",
                              }}
                            >
                              To
                            </InputLabel>
                            <DatePicker
                              selected={parseISO(period.end)}
                              onChange={(date: Date | null) =>
                                this.setEndDate(date, period)
                              }
                              dateFormat="dd/MM/yyyy"
                              minDate={parseISO(period.start)}
                              popperPlacement="bottom"
                              customInput={<TextField fullWidth size="small" />} // Use TextField for consistent styling
                            />
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box sx={{ paddingRight: "1rem" }}>
                  <Box
                    className="form-group row"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        marginRight: "1rem",
                        marginLeft: "3rem",
                      }}
                    >
                      On
                    </Typography>
                    {days.map((day, dayIdx) => (
                      <FormControlLabel
                        key={day}
                        control={
                          <Checkbox
                            checked={this.state.weekDays[dayIdx]}
                            onChange={() => this.handleWeekDayChange(dayIdx)}
                            name={day}
                            color="primary"
                            sx={{ "& .MuiSvgIcon-root": { fontSize: 15 } }}
                          />
                        }
                        label={day}
                      />
                    ))}
                  </Box>
                </Box>
                <Box
                  className="row align-items-center"
                  display="flex"
                  alignItems="center"
                >
                  <Box
                    className="col col-sm-3"
                    marginLeft="3rem"
                    marginRight="5rem"
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    <Button onClick={this.addPeriod}>
                      <AddIcon />
                    </Button>
                    {this.state.periods.length > 1 && (
                      <Button onClick={this.removePeriod}>
                        <RemoveIcon />
                      </Button>
                    )}
                  </Box>
                </Box>
                <Box
                  className="row align-items-center"
                  sx={{
                    marginTop: "1rem",
                    marginLeft: "2rem",
                    marginRight: "4rem",
                    display: "flex",
                    gap: "1rem",
                    alignItems: "flex-start",
                  }}
                >
                  <Box sx={{ flexGrow: 1 }}>
                    <FormControl
                      fullWidth
                      variant="outlined"
                      className="form-group"
                      size="small"
                    >
                      <InputLabel>Update</InputLabel>
                      <Select
                        name="level"
                        value={this.state.level}
                        onChange={(e) => this.handleNativeSelect(e)}
                        label="Update" // Ensure label is linked
                        // ... other Select props
                      >
                        <MenuItem value="baseRate">Base Rate</MenuItem>
                        {hasOWSBRRow(this.props.activeHotel) && (
                          <MenuItem value="owsBaseRate">OWS Base Rate</MenuItem>
                        )}
                        <MenuItem value="OVERIDE_OWS">OWS Override</MenuItem>
                        <MenuItem value="setRate">Set Rate</MenuItem>
                        <MenuItem value="APPLY_STOP_SELL">+ Stop Sell</MenuItem>
                        <MenuItem value="REMOVE_STOP_SELL">
                          - Stop Sell
                        </MenuItem>
                        <MenuItem value="APPLY_MIN_DAYS#2">
                          + Min LOS 2
                        </MenuItem>
                        <MenuItem value="REMOVE_MIN_DAYS">- Min LOS</MenuItem>
                        <MenuItem value="APPLY_CLOSED_TO_ARRIVAL">
                          + Closed to Arrival
                        </MenuItem>
                        <MenuItem value="REMOVE_CLOSED_TO_ARRIVAL">
                          - Closed to Arrival
                        </MenuItem>
                        <MenuItem value="APPLY_CLOSED_TO_DEPARTURE">
                          + Closed to Departure
                        </MenuItem>
                        <MenuItem value="REMOVE_CLOSED_TO_DEPARTURE">
                          - Closed to Departure
                        </MenuItem>
                        {/* Render each reward MenuItem conditionally */}
                        {this.props.activeHotel.meta.hasRewards && (
                          <MenuItem key="CLOSE_REWARDS" value="CLOSE_REWARDS">
                            Close Rewards
                          </MenuItem>
                        )}
                        {this.props.activeHotel.meta.hasRewards && (
                          <MenuItem key="OPEN_REWARDS" value="OPEN_REWARDS">
                            Open Rewards
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box
                    sx={{
                      display:
                        this.state.property === "baseRate" ||
                        this.state.property === "setRate" ||
                        this.state.property === "owsBaseRate"
                          ? "block"
                          : "none",
                      flexGrow: 1,
                    }}
                  >
                    <TextField
                      id="baseRate"
                      name="baseRate"
                      label={getRateLabel(this.state.property)}
                      variant="outlined"
                      size="small"
                      sx={{
                        backgroundColor: !hasPermission(ACTIONS.PUBLISH)
                          ? (theme) => theme.palette.action.disabledBackground
                          : "inherit",
                        width: "100%",
                      }}
                      type="number"
                      placeholder=""
                      value={this.state.baseRate}
                      InputProps={{ inputProps: { step: 1 } }}
                      onChange={(e) => this.handleInputChange(e)}
                      className={getFieldClasses(this.state, "baseRate")}
                      error={!!this.state.errors.baseRate}
                      helperText={this.state.errors.baseRate}
                    />
                  </Box>
                  {this.state.property === "OVERIDE_OWS" && (
                    <Box sx={{ flexGrow: 1 }}>
                      <FormControl
                        fullWidth
                        variant="outlined"
                        className="form-group"
                        size="small"
                      >
                        <InputLabel>Label</InputLabel>
                        <Select
                          id="property-label"
                          sx={{
                            backgroundColor: !hasPermission(ACTIONS.PUBLISH)
                              ? (theme) =>
                                  theme.palette.action.disabledBackground
                              : "inherit",
                          }}
                          fullWidth
                          name="owslabel"
                          label="Label"
                          variant="outlined"
                          value={this.state.owslabel}
                          onChange={(e) =>
                            this.setState({ owslabel: e.target.value })
                          }
                        >
                          <MenuItem value={this.state.defaultOwsLabel}>
                            {this.state.defaultOwsLabel}
                          </MenuItem>
                          {owsOverrideOptions &&
                          Array.isArray(owsOverrideOptions)
                            ? owsOverrideOptions.map((option, index) => (
                                <MenuItem key={index} value={option.key}>
                                  {option.key}
                                </MenuItem>
                              ))
                            : null}
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                </Box>
                <Box sx={{ mt: 2 }}>
                  <BulkUpdatePreview
                    hotelId={this.props.activeHotel.hotelId}
                    rate={
                      !(
                        this.state.property === "baseRate" ||
                        this.state.property === "owsBaseRate" ||
                        this.state.property === "CLOSE_REWARDS" ||
                        this.state.property === "OPEN_REWARDS" ||
                        this.state.property === "OVERIDE_OWS"
                      )
                        ? 100
                        : this.state.baseRate
                    }
                    selectable={
                      this.state.property !== "baseRate" &&
                      this.state.property !== "owsBaseRate" &&
                      this.state.property !== "CLOSE_REWARDS" &&
                      this.state.property !== "OPEN_REWARDS" &&
                      this.state.property !== "OVERIDE_OWS"
                    }
                    manualOnly={this.state.property === "setRate"}
                    clearTrigger={this.state.property}
                    periods={this.state.periods}
                    weekDays={this.state.weekDays}
                    onSelection={(selections) => {
                      console.log(
                        "BulkUpdate received selection update:",
                        selections,
                      );
                      this.setState({ restrictionSelections: selections });
                    }}
                  />
                </Box>
              </Box>
            </ThemeProvider>,
          ]}
          maxWidth="md"
          action={() => {
            const formData = {
              property: this.state.property,
              baseRate: this.state.baseRate,
              weekDays: this.state.weekDays,
              owslabel: this.state.owslabel,
            };

            // Validate before applying
            const baseRateError = this.validate(this.state.baseRate);
            const hasValidSelections = this.hasValidSelections();

            if (
              (this.state.property === "baseRate" ||
                this.state.property === "setRate" ||
                this.state.property === "owsBaseRate") &&
              baseRateError
            ) {
              this.setState({ errors: { baseRate: baseRateError } });
              toast("Please check the highlighted field", { type: "error" });
            } else if (!hasValidSelections) {
              toast("Please select at least one room type or rate plan", {
                type: "error",
              });
            } else {
              this.setState({ errors: {} }); // Clear errors if valid
              this.applyToYieldSheet(formData);
            }
          }}
          actionName="Apply"
          actionDisabled={isApplyDisabled()} // Add this prop to disable the button
          cancelAction={() => this.props.popupState(false)}
          cancelName="Back to yield sheet"
        />
      </>
    );
  }

  private prepareRewardsPayload(data: FormState): IBulkRewardsUpdate[] {
    const hotelId = this.props.activeHotel.hotelId;
    const dow: Dow = days.reduce(
      (acc, day, idx) => ({ ...acc, [day]: data.weekDays[idx] }),
      {},
    );

    const res = this.state.periods.map((p) => {
      return {
        start: p.start,
        end: p.end,
        hotelId,
        dow,
        active: data.property.indexOf("OPEN") !== -1,
      };
    });
    return res;
  }
  private prepareOverideOwsPayload(
    data: PublishPayload,
  ): BulkOwsOverridePayload[] {
    const hotelId = this.props.activeHotel.hotelId;
    const dow: Dow = days.reduce(
      (acc, day, idx) => ({ ...acc, [day]: data.weekDays[idx] }),
      {},
    );

    const res = this.state.periods.map((p) => {
      return {
        ...data,
        start: p.start,
        end: p.end,
        baseRate: data.baseRate,
        dow,
        hotelId,
        label: data.owslabel,
      };
    });
    return res;
  }
  private prepareBaseRatePayload(data: PublishPayload): BulkUpdatePayload[] {
    const hotelId = this.props.activeHotel.hotelId;
    const dow: Dow = days.reduce(
      (acc, day, idx) => ({ ...acc, [day]: data.weekDays[idx] }),
      {},
    );

    const res = this.state.periods.map((p) => {
      return {
        ...data,
        start: p.start,
        end: p.end,
        baseRate: data.baseRate,
        dow,
        hotelId,
      };
    });
    return res;
  }
  private prepareOWSBaseRatePayload(data: PublishPayload): BulkUpdatePayload[] {
    const hotelId = this.props.activeHotel.hotelId;
    const dow: Dow = days.reduce(
      (acc, day, idx) => ({ ...acc, [day]: data.weekDays[idx] }),
      {},
    );

    const res = this.state.periods.map((p) => {
      return {
        weekDays: data.weekDays,
        baseRate: 0,
        start: p.start,
        end: p.end,
        owsBaseRate: data.baseRate,
        dow,
        hotelId,
      };
    });
    return res;
  }
  private prepareSetRatePayload(data: FormState): IBulkManualRateUpdate[] {
    const hotel = this.props.activeHotel;
    const hotelId = hotel.hotelId;
    const dow: Dow = days.reduce(
      (acc, day, idx) => ({ ...acc, [day]: data.weekDays[idx] }),
      {},
    );

    const results = this.state.periods.map((p) => {
      const v: IManualRateUpdate[] = [];
      return {
        dow,
        updates: v,
        hotelId,
        start: p.start,
        end: p.end,
      };
    });

    results.forEach((res) => {
      Object.keys(this.state.restrictionSelections)
        .map((roomtype) => {
          const i: IManualRateUpdate[] = [];
          const rps = Object.keys(
            this.state.restrictionSelections[roomtype].ratePlans,
          ).filter(
            (rp) =>
              this.state.restrictionSelections[roomtype].ratePlans[rp] === true,
          );
          rps.forEach((rp: string) => {
            i.push({
              hotelId,
              start: res.start,
              end: res.end,
              rate: data.baseRate,
              invTypeCode: roomtype,
              ratePlanID: rp,
            });
          });
          return i;
        })
        .forEach((value) => {
          res.updates.push(...value);
        });
    });
    return results;
  }

  private prepareRestrictionsPayload(
    data: FormState,
  ): Promise<IRestrictionsUpdate[]> {
    return new Promise((resolve) => {
      const hotel = this.props.activeHotel;
      const hotelId = hotel.hotelId;
      const actionName: "apply" | "remove" = !data.property.indexOf("APPLY_")
        ? "apply"
        : "remove";
      const daycount = data.property.indexOf("#")
        ? data.property.split("#")[1]
        : 0;
      const restrictionKey = data.property
        .replace(/^[A-Z]*_/, "")
        .split("#")[0] as RestrictionTypeKeys;
      const restrictionType = restrictions.TYPE[restrictionKey];
      const dow: Dow = days.reduce(
        (acc, day, idx) => ({ ...acc, [day]: data.weekDays[idx] }),
        {},
      );
      // Prepare one payload per period with combined applies
      const updates: IRestrictionsUpdate[] = [];

      // Compute final dayCount once (ensure >=1 for min/max days)
      const computedDayCount =
        (restrictionType === "min_days" || restrictionType === "max_days") &&
        (daycount === undefined ||
          daycount === null ||
          daycount === "" ||
          Number(daycount) === 0)
          ? 1
          : Number(daycount);

      this.state.periods.forEach((p) => {
        const applies: { roomType: string; ratePlans?: string[] }[] = [];

        // Iterate over all roomTypes in selections
        Object.keys(this.state.restrictionSelections).forEach((roomType) => {
          const roomTypeData = this.state.restrictionSelections[roomType];

          if (roomTypeData.selected) {
            // Whole room type selected -> room-level restriction
            applies.push({ roomType });
          } else {
            // Collect selected rate plans for this room type, excluding disallowed ones
            const selectedRPs = Object.keys(roomTypeData.ratePlans || {})
              .filter((rp) => roomTypeData.ratePlans[rp])
              .filter(
                (rp) =>
                  !hotel.ratePlans[rp].restrictionExclusions ||
                  hotel.ratePlans[rp].restrictionExclusions.indexOf(
                    restrictionType,
                  ) === -1,
              );

            if (selectedRPs.length > 0) {
              applies.push({ roomType, ratePlans: selectedRPs });
            }
          }
        });

        if (applies.length > 0) {
          updates.push({
            hotelId,
            start: p.start,
            end: p.end,
            dow,
            type: restrictionType,
            active: actionName === "apply",
            applies,
            dayCount: computedDayCount,
          });
        }
      });

      resolve(updates);
    });
  }
}

const mapStateToProps = (state: any) => ({
  activeHotel: state.hotelData.activeHotel,
  user: state.user,
});

export default connect(mapStateToProps)(BulkUpdate);

function hasOWSBRRow(hotel: any) {
  return hotel.cm.channelConfig && hotel.cm.channelConfig.ows;
}

export function getRateLabel(input: string) {
  switch (input) {
    case "baseRate":
      return "Base Rate";
    case "owsBaseRate":
      return "OWS Base Rate";
    default:
      return "Set Rate";
  }
}
